/**
 * Automation Service - API client for Automation Rules
 */

import { apiClient } from './api';

// ===================================================================
// Types (mirrors backend api/src/domain/models/automation-rule.ts)
// ===================================================================

export enum AutomationConditionType {
  BELOW = 'below',
  ABOVE = 'above',
  OUTSIDE_RANGE = 'outside_range',
  INSIDE_RANGE = 'inside_range',
  EQUALS = 'equals',
  AND = 'and',
  OR = 'or',
}

export enum SystemMode {
  NORMAL = 'normal',
  MAINTENANCE = 'maintenance',
  WATER_CHANGE = 'water_change',
  CALIBRATION = 'calibration',
  EMERGENCY_STOP = 'emergency_stop',
}

export enum AutomationActionType {
  ACTUATOR_ON = 'actuator_on',
  ACTUATOR_OFF = 'actuator_off',
  ACTUATOR_TIMED = 'actuator_timed',
  ALERT = 'alert',
}

/**
 * Action step types for sequences
 */
export enum ActionStepType {
  ACTUATOR_ON = 'actuator_on',
  ACTUATOR_OFF = 'actuator_off',
  ACTUATOR_TIMED = 'actuator_timed',
  WAIT = 'wait',
  ALERT = 'alert',
  CONTROL_ACTUATOR = 'control_actuator',
  DOSE = 'dose',
  MEASURE = 'measure',
  NOTIFY = 'notify',
  CONTROL_RULE = 'control_rule',
  DOSE_LOOP = 'dose_loop',
}

/**
 * Single step in an action sequence
 */
export interface ActionStep {
  order: number;
  step_type: ActionStepType;
  actuator_id?: string;
  /** Frontend-only: multiple actuator IDs — expanded to N steps before API call */
  actuator_ids?: string[];
  duration_sec?: number;
  /**
   * Výkon aktuátoru v procentech (0–100) pro zapnutí i časovaný běh.
   *
   * POZOR na název: backend čte `pwm_percent` (ActionStep v api/src/domain/models/
   * automation-rule.ts). Frontend sem dřív zapisoval `pwm_value` přes `as any`,
   * takže se pole nikdy nespárovalo — backend dosadil výchozích 100 % a čerpadlo
   * jelo naplno bez ohledu na to, co uživatel nastavil. TypeScript to kvůli tomu
   * přetypování nemohl odhalit, proto je pole nově součástí typu.
   */
  pwm_percent?: number;
  alert_message?: string;
  dose_ml?: number;
  sensor_type?: string;
  notify_message?: string;
  target_rule_id?: number;
  rule_action?: 'enable' | 'disable' | 'trigger';

  // Dose-loop fields
  loop_max_iterations?: number;
  loop_wait_sec?: number;
  loop_recheck_sensor?: string;
  loop_target_value?: number;
  loop_target_condition?: 'below' | 'above' | 'inside_range';
  loop_target_min?: number;
  loop_target_max?: number;
}

export interface RuleCondition {
  id?: string;
  sensor_type?: string;
  condition_type: AutomationConditionType;
  threshold_value?: number;
  threshold_min?: number;
  threshold_max?: number;
  hysteresis?: number;
  conditions?: RuleCondition[];
}

export interface AutomationRule {
  id: number;
  device_id: string;
  name: string;
  description?: string;

  // Trigger
  sensor_type: string;
  condition_type: AutomationConditionType;
  threshold_value?: number;
  threshold_min?: number;
  threshold_max?: number;
  hysteresis: number;
  blocked_by_modes: SystemMode[];
  compound_condition?: RuleCondition;

  // Sustained trigger (debounce)
  sustained_seconds: number;
  condition_met_since?: string;

  // Simple Action
  action_type: AutomationActionType;
  action_actuator_id?: string;
  action_duration_sec?: number;
  action_params: Record<string, any>;

  // Action Sequence (advanced)
  action_steps: ActionStep[];

  // Sequence Execution State
  sequence_running: boolean;
  sequence_current_step: number;
  sequence_started_at?: string;

  // Settings
  enabled: boolean;
  priority: number;
  cooldown_seconds: number;

  // State
  is_triggered: boolean;
  last_triggered_at?: string;
  trigger_count: number;

  created_at: string;
  updated_at: string;
}

export interface CreateAutomationRuleDTO {
  device_id: string;
  name: string;
  description?: string;
  sensor_type: string;
  condition_type: AutomationConditionType;
  threshold_value?: number;
  threshold_min?: number;
  threshold_max?: number;
  hysteresis?: number;
  sustained_seconds?: number;
  blocked_by_modes?: SystemMode[];
  compound_condition?: RuleCondition;
  action_type: AutomationActionType;
  action_actuator_id?: string;
  action_duration_sec?: number;
  action_params?: Record<string, any>;
  action_steps?: ActionStep[]; // Optional sequence of steps
  enabled?: boolean;
  priority?: number;
  cooldown_seconds?: number;
  [key: string]: any;
}

export interface UpdateAutomationRuleDTO {
  name?: string;
  description?: string;
  sensor_type?: string;
  condition_type?: AutomationConditionType;
  threshold_value?: number;
  threshold_min?: number;
  threshold_max?: number;
  hysteresis?: number;
  sustained_seconds?: number;
  blocked_by_modes?: SystemMode[];
  compound_condition?: RuleCondition;
  action_type?: AutomationActionType;
  action_actuator_id?: string;
  action_duration_sec?: number;
  action_params?: Record<string, any>;
  action_steps?: ActionStep[]; // Optional sequence of steps
  enabled?: boolean;
  priority?: number;
  cooldown_seconds?: number;
  [key: string]: any;
}

// ===================================================================
// Historie běhů + analytika (automation_rule_events)
// ===================================================================

export interface RuleEvent {
  id: number;
  rule_id: number | null;
  device_id: string;
  rule_name: string;
  trigger_source: 'sensor' | 'schedule' | 'manual' | string;
  sensor_type?: string;
  sensor_value?: number;
  action_type?: string;
  status: 'running' | 'success' | 'failed';
  total_steps?: number;
  current_step?: number;
  expected_finish_at?: string;
  error_message?: string;
  started_at: string;
  finished_at?: string;
}

export interface RuleAnalytics {
  total_runs: number;
  success_runs: number;
  failed_runs: number;
  by_day: { day: string; count: number; success: number; failed: number }[];
  top_rules: { rule_id: number | null; rule_name: string; count: number }[];
}

export interface RuleEventsPage {
  events: RuleEvent[];
  total: number;
}

// ===================================================================
// API Client
// ===================================================================

export const automationService = {
  /**
   * Get all rules for a device
   */
  getRules: async (deviceId: string): Promise<AutomationRule[]> => {
    return apiClient.get<AutomationRule[]>(`/automation-rules/device/${deviceId}`);
  },

  /**
   * Get single rule
   */
  getRule: async (ruleId: number): Promise<AutomationRule> => {
    return apiClient.get<AutomationRule>(`/automation-rules/${ruleId}`);
  },

  /**
   * Create new rule
   */
  createRule: async (data: CreateAutomationRuleDTO): Promise<AutomationRule> => {
    return apiClient.post<AutomationRule>(`/automation-rules`, data);
  },

  /**
   * Update existing rule
   */
  updateRule: async (ruleId: number, data: UpdateAutomationRuleDTO): Promise<AutomationRule> => {
    return apiClient.put<AutomationRule>(`/automation-rules/${ruleId}`, data);
  },

  /**
   * Delete rule
   */
  deleteRule: async (ruleId: number): Promise<void> => {
    return apiClient.delete(`/automation-rules/${ruleId}`);
  },

  /**
   * Toggle rule enabled status
   */
  toggleRule: async (ruleId: number, enabled: boolean): Promise<AutomationRule> => {
    return apiClient.put<AutomationRule>(`/automation-rules/${ruleId}`, { enabled });
  },

  /**
   * Historie běhů pravidel pro zařízení
   */
  getEvents: async (
    deviceId: string,
    opts?: { limit?: number; offset?: number; ruleId?: number; status?: string }
  ): Promise<RuleEventsPage> => {
    const q = new URLSearchParams();
    if (opts?.limit !== undefined) q.set('limit', String(opts.limit));
    if (opts?.offset !== undefined) q.set('offset', String(opts.offset));
    if (opts?.ruleId !== undefined) q.set('ruleId', String(opts.ruleId));
    if (opts?.status) q.set('status', opts.status);
    const qs = q.toString();
    return apiClient.get<RuleEventsPage>(`/automation-rules/events/device/${deviceId}${qs ? `?${qs}` : ''}`);
  },

  /**
   * Právě běžící pravidla (nedoběhlé sekvence / časované akce v okně)
   */
  getRunningEvents: async (deviceId: string): Promise<RuleEvent[]> => {
    return apiClient.get<RuleEvent[]>(`/automation-rules/running/device/${deviceId}`);
  },

  /**
   * Analytická data běhů pravidel
   */
  getAnalytics: async (deviceId: string, days = 7): Promise<RuleAnalytics> => {
    return apiClient.get<RuleAnalytics>(`/automation-rules/analytics/device/${deviceId}?days=${days}`);
  },
};
